import { TempDemoManager } from '@/utils/tempDemoManager';
import React, { useEffect, useState } from 'react';

interface AwsInitializerProps {
  children: React.ReactNode;
}

/**
 * Component that initializes AWS configuration before rendering children
 */
const AwsInitializer: React.FC<AwsInitializerProps> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAws = async () => {
      try {
        console.debug('🔧 Initializing AWS Config Service...');


        // Initialize temporary demo manager
        TempDemoManager.initialize();
        console.debug('✅ Temporary Demo Manager initialized');

        setIsInitialized(true);
        console.debug('✅ AWS Config Service initialized successfully');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('❌ Failed to initialize AWS Config Service:', errorMessage);
        setError(errorMessage);
      }
    };

    initializeAws();
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="max-w-md p-6 bg-white rounded-lg shadow-lg border border-red-200">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">AWS Configuration Error</h3>
            </div>
          </div>
          <div className="text-sm text-red-700">
            <p className="mb-2">Failed to initialize AWS services:</p>
            <p className="font-mono text-xs bg-red-100 p-2 rounded border">{error}</p>
            <p className="mt-2">Please check your environment configuration and try refreshing the page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing AWS services...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AwsInitializer;